import { useState, useRef, useEffect, useMemo } from "react";

interface WheelPickerProps {
  value: number;
  onChange: (v: number) => void;
  label: string;
  max: number;
  itemHeight?: number;
  isOpen?: boolean;
}

export function WheelPicker({ value, onChange, label, max, itemHeight = 44, isOpen = true }: WheelPickerProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [scrollTop, setScrollTop] = useState(0);
  const isDragging = useRef(false);
  const startY = useRef(0);
  const startScrollTop = useRef(0);

  const options = useMemo(() => Array.from({ length: max + 1 }, (_, i) => i), [max]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = value * itemHeight;
      setScrollTop(value * itemHeight);
    }
  }, [isOpen, value, itemHeight]);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const top = e.currentTarget.scrollTop;
    setScrollTop(top);
    const index = Math.round(top / itemHeight);
    if (index >= 0 && index <= max && index !== value) {
      onChange(index);
    }
  };

  const onMouseDown = (e: React.MouseEvent) => {
    isDragging.current = true;
    startY.current = e.pageY;
    startScrollTop.current = scrollRef.current?.scrollTop || 0;
    if (scrollRef.current) scrollRef.current.style.scrollSnapType = 'none';
  };

  const onMouseMove = (e: MouseEvent) => {
    if (!isDragging.current || !scrollRef.current) return;
    const dy = e.pageY - startY.current;
    scrollRef.current.scrollTop = startScrollTop.current - dy;
  };

  const onMouseUp = () => {
    if (isDragging.current && scrollRef.current) {
      isDragging.current = false;
      scrollRef.current.style.scrollSnapType = 'y mandatory';
      const top = scrollRef.current.scrollTop;
      const index = Math.round(top / itemHeight);
      scrollRef.current.scrollTo({ top: index * itemHeight, behavior: 'smooth' });
    }
  };

  useEffect(() => {
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, []);

  return (
    <div className="flex-1 flex flex-col items-center h-full relative touch-none">
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        onMouseDown={onMouseDown}
        className="w-full h-full overflow-y-auto scrollbar-none snap-y snap-mandatory cursor-grab active:cursor-grabbing select-none relative z-10"
        style={{ 
          paddingTop: `${(220 - itemHeight) / 2}px`, 
          paddingBottom: `${(220 - itemHeight) / 2}px` 
        }}
      >
        {options.map((opt) => {
          const distance = Math.abs((opt * itemHeight) - scrollTop);
          const normalizedDistance = Math.min(distance / (itemHeight * 2.5), 1);
          const rotateX = ((opt * itemHeight) - scrollTop) / 1.5;
          const opacity = 1 - (normalizedDistance * 0.9);
          const scale = 1 - (normalizedDistance * 0.3);

          return (
            <div
              key={opt}
              className="flex items-center justify-center snap-center shrink-0 pointer-events-none"
              style={{
                height: `${itemHeight}px`,
                transform: `rotateX(${rotateX}deg) scale(${scale})`,
                opacity: opacity,
              }}
            >
              <span className="text-2xl font-bold tabular-nums text-foreground">
                {String(opt).padStart(2, '0')}
              </span>
            </div>
          );
        })}
      </div>
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-primary/10 backdrop-blur-md border border-primary/20 pointer-events-none z-20 flex items-center justify-center">
        <span className="text-[9px] font-black uppercase tracking-[0.2em] text-primary whitespace-nowrap ml-[0.1em]">
          {label}
        </span>
      </div>
    </div>
  );
}
